import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames/bind';
import { fromPairs, get, uniq } from 'lodash';
import { Button } from 'tuui';
import uuid from 'uuid/v1';
import { DropdownMenu } from 'taboola-ultimate-ui';
import { tabId } from 'modules/taboola-common-frontend-modules/app-events-aggregator';
import { useChatAgentSessionId } from 'modules/taboola-common-frontend-modules/chat-agent/hooks/useChatAgentSessionId';
import { useTools } from 'modules/taboola-common-frontend-modules/chat-agent/hooks/useTools';
import { WebSocketContext } from 'modules/taboola-common-frontend-modules/web-socket-provider/component/WebSocketContext';
import { CHAT_AGENT_COMPONENT_TYPE } from '../../../../modules/taboola-common-frontend-modules/chat-agent/config/chatAgentComponentType';
import { useChatDevTool } from '../../hooks/useChatDevTool';
import { extractParamsFromFunction } from './utils';
import styles from './ChatDevTool.module.scss';

const classNameBuilder = classnames.bind(styles);

/**
 * Creates a template object with default values based on parameter names
 * @param {Array} params - Array of parameter names
 * @returns {Object} - Template object with default values
 */
const TEMPLATE_DEFAULTS = {
    message: 'Hello, this is a test message',
    budgetDesktop: 150,
    budgetMobile: 100,
    quickAnswers: [
        { label: 'Send message', methodName: 'chat.sendMessage', args: { message: 'hello message from user' } },
        { label: 'Set 115$ Budget', methodName: 'campaigns.setBudgetComponent', args: { budgetDesktop: 115 } },
        { label: 'Add Credit Card', methodName: 'billing.openCreateCreditCard', args: {} },
    ],
    images: [
        'https://images.taboola.com/taboola/image/fetch/f_jpg%2Cq_auto%2Ch_250%2Cw_400%2Cc_fill/https%3A%2F%2Fwww.idlethumbs.net%2Fassets%2Fimg%2Fepisodes%2F464__header.jpg',
        'https://images.taboola.com/taboola/image/fetch/f_jpg%2Cq_auto%2Ch_250%2Cw_400%2Cc_fill/https%3A%2F%2Fwww.idlethumbs.net%2Fassets%2Fimg%2Fepisodes%2F464__header.jpg',
        'https://images.taboola.com/taboola/image/fetch/f_jpg%2Cq_auto%2Ch_250%2Cw_400%2Cc_fill/https%3A%2F%2Fwww.idlethumbs.net%2Fassets%2Fimg%2Fepisodes%2F464__header.jpg',
    ],
    csvData:
        'Campaign,Impressions,Clicks,CTR,CPC\nSample Campaign 1,10000,500,5.0%,$2.00\nSample Campaign 2,20000,1000,5.0%,$1.80\nSample Campaign 3,15000,750,5.0%,$1.90',
    baseFilename: 'sample_report',
    mimeType: 'image/jpeg',
    reportConfig: {
        reportReference: {
            reportType: 'PERFORMANCE',
        },
        columns: {
            values: [
                {
                    nodeReference: {
                        name: 'PERFORMANCE_REPORT.CAMPAIGN.CAMPAIGN_NAME',
                    },
                    sortIndex: 0,
                    sortOrder: 'DESC',
                },
                {
                    nodeReference: {
                        name: 'PERFORMANCE_REPORT.CAMPAIGN.CAMPAIGN_ID',
                    },
                    sortIndex: null,
                    sortOrder: null,
                },
                {
                    nodeReference: {
                        name: 'PERFORMANCE_REPORT.METRICS.CLICKS',
                    },
                    sortIndex: 1,
                    sortOrder: 'DESC',
                },
                {
                    nodeReference: {
                        name: 'PERFORMANCE_REPORT.METRICS.CONVERSIONS_NUM',
                    },
                    sortIndex: 2,
                    sortOrder: 'DESC',
                },
                {
                    nodeReference: {
                        name: 'PERFORMANCE_REPORT.METRICS.SPENT',
                    },
                    sortIndex: 3,
                    sortOrder: 'DESC',
                },
                {
                    nodeReference: {
                        name: 'PERFORMANCE_REPORT.METRICS.CPA',
                    },
                    sortIndex: 4,
                    sortOrder: 'ASC',
                },
                {
                    nodeReference: {
                        name: 'PERFORMANCE_REPORT.METRICS.CVR',
                    },
                    sortIndex: 5,
                    sortOrder: 'DESC',
                },
                {
                    nodeReference: {
                        name: 'PERFORMANCE_REPORT.METRICS.CTR',
                    },
                    sortIndex: 6,
                    sortOrder: 'DESC',
                },
                {
                    nodeReference: {
                        name: 'PERFORMANCE_REPORT.METRICS.VISIBLE_IMPRESSIONS',
                    },
                    sortIndex: 7,
                    sortOrder: 'DESC',
                },
                {
                    nodeReference: {
                        name: 'PERFORMANCE_REPORT.ACCOUNT.ACCOUNT_ID',
                    },
                    sortIndex: null,
                    sortOrder: null,
                },
            ],
        },
        filters: {
            values: [
                {
                    nodeReference: {
                        name: 'PERFORMANCE_REPORT.TIME_UNITS.DAY',
                    },
                    filterType: 'VALUE_FILTER',
                    filterOperator: 'BETWEEN',
                    filterValues: ['2025-07-01', '2025-10-20'],
                },
                {
                    nodeReference: {
                        name: 'PERFORMANCE_REPORT.ACCOUNT.ACCOUNT_ID',
                    },
                    filterType: 'PREDEFINED_FILTER',
                    filterOperator: 'EQUALS',
                    filterValues: ['32'],
                },
            ],
        },
    },
};

const generateTemplateFromParams = params => {
    if (!params || params.length === 0) return {};

    return fromPairs(params.map(param => [param, TEMPLATE_DEFAULTS[param] ?? '']));
};

export const ChatDevTool = () => {
    const [sessionId] = useChatAgentSessionId();
    const [componentName, setComponentName] = useState('');
    const [value, setValue] = useState('');
    const [showChatDevTool] = useChatDevTool();
    const { namespaces } = useTools({});
    const { __onMessage_ONLY_FOR_DEV_PURPOSES: onMessage } = useContext(WebSocketContext);

    // Create dropdown options from available tools
    const toolOptions = useMemo(() => {
        return Object.entries(namespaces).flatMap(([namespace, tools]) =>
            Object.keys(tools).map(toolName => ({
                value: `${namespace}.${toolName}`,
                label: `${namespace}.${toolName}`,
            }))
        );
    }, [namespaces]);

    // Generate template based on the selected tool
    const generateTemplate = useCallback(
        toolName => {
            try {
                const [namespace, method] = toolName.split('.');
                if (!namespace || !method) return '{}';

                const toolConfig = get(namespaces, toolName);
                if (!toolConfig) {
                    return '{}';
                }

                // Extract parameters from both method types
                const toolParams = extractParamsFromFunction(toolConfig);

                // Merge parameters and create template
                const allParams = uniq([...toolParams]);
                const template = generateTemplateFromParams(allParams);

                return JSON.stringify(template, null, 2);
            } catch (error) {
                console.error('Error generating template:', error);
                return '{}';
            }
        },
        [namespaces]
    );

    // Handle textarea change event
    const handleTextareaChange = useCallback(e => {
        setValue(e.target.value);
    }, []);

    // Simulate sending WebSocket message
    const simulateWebSocketMessage = useCallback(() => {
        try {
            // EditableText might include extra whitespace or newlines, ensure clean JSON parsing
            const jsonValue = value.trim();
            JSON.parse(jsonValue); // Validate JSON format

            const messageData = {
                data: JSON.stringify({
                    message: {
                        id: uuid(),
                        sessionId,
                        tabId,
                        flows: [],
                        componentData: {
                            value: jsonValue || '',
                            componentName: componentName || CHAT_AGENT_COMPONENT_TYPE.TEXT,
                        },
                    },
                }),
            };

            onMessage(messageData);
        } catch (error) {
            console.error('Invalid JSON format for debug message:', error);
        }
    }, [componentName, onMessage, sessionId, value]);

    // Update value when componentName changes
    useEffect(() => {
        if (componentName) {
            const template = generateTemplate(componentName);
            setValue(template);
        }
    }, [componentName, generateTemplate]);

    if (!showChatDevTool) {
        return null;
    }

    return (
        <div className={classNameBuilder('debug-controls')}>
            <div>
                <DropdownMenu
                    options={toolOptions}
                    selectedValueObject={{ value: componentName }}
                    onChange={({ value }) => setComponentName(value)}
                    placeholder="Select tool or component"
                    searchable
                    menuPlacement="top"
                />
            </div>
            <div>
                <textarea
                    value={value}
                    onChange={handleTextareaChange}
                    placeholder="JSON object with required parameters..."
                    className={classNameBuilder('json-editor', 'json-editor-input')}
                    rows={10}
                />
            </div>
            <Button size={Button.size.md} variant={Button.variant.primary} onClick={simulateWebSocketMessage}>
                Apply
            </Button>
        </div>
    );
};
